import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { StressFaceIcon } from '../../components/sessionResult/StressFaceIcons';
import {
  KakaoIcon,
  MetricIconClock,
  MetricIconHeart,
  MetricIconHrv,
} from '../../components/sessionResult/SessionMetricIcons';
import sessionCompleteIcon from '../../assets/images/sessionResult/sessionCompleteIcon.png';
import {
  displayMetric,
  isKakaoShareEnabled,
  parseSessionResultQuery,
  type SessionResultQueryV1,
} from '../../utils/sessionResult/parseSessionResultQuery';
import {
  formatSessionRecordDate,
  getStressTheme,
} from '../../utils/sessionResult/stressTheme';
import {
  getSessionResultShareUrl,
  sessionShareFeedbackMessage,
  shareSessionResultPage,
} from '../../utils/sessionResult/shareSessionResultPage';
import {
  SESSION_RESULT_OG_IMAGE,
  SESSION_RESULT_OG_IMAGE_HEIGHT,
  SESSION_RESULT_OG_IMAGE_WIDTH,
  SESSION_RESULT_SITE_NAME,
} from '../../utils/sessionResult/sessionResultSeo';
import { openSoundPillApp } from '../../utils/openSoundPillApp';
import './session-result-page.css';

type VitalCardProps = {
  icon: ReactNode;
  iconBg: string;
  ringColor: string;
  label: string;
  value: ReactNode;
  subLabel?: string;
  accent: string;
};

function VitalCard({ icon, iconBg, ringColor, label, value, subLabel, accent }: VitalCardProps) {
  return (
    <article className="qr-session-result__card">
      <div className="qr-session-result__card-icon" style={{ background: iconBg }}>
        {icon}
      </div>
      <div className="qr-session-result__card-ring" style={{ borderColor: ringColor }} aria-hidden />
      <div className="qr-session-result__card-body">
        <p className="qr-session-result__card-label">{label}</p>
        <div className="qr-session-result__card-value">{value}</div>
        {subLabel ? (
          <p className="qr-session-result__card-sub" style={{ color: accent }}>
            {subLabel}
          </p>
        ) : null}
      </div>
    </article>
  );
}

type SessionResultContentProps = {
  data: SessionResultQueryV1;
  autoKakaoShare: boolean;
};

function SessionResultContent({ data, autoKakaoShare }: SessionResultContentProps) {
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const autoShareTriggered = useRef(false);

  const theme = getStressTheme(data.stress);
  const recordDate = formatSessionRecordDate(data.t);
  const pageTitle = `나의 스트레스 완화 세션 | SoundPill`;
  const shareUrl = getSessionResultShareUrl();
  const shareDescription = data.sl || data.hl;

  const themeStyle = {
    '--qr-hero-glow': theme.heroGlow,
    '--qr-hero-shadow': theme.heroShadow,
    '--qr-accent': theme.accent,
    '--qr-icon-bg': theme.iconBg,
    '--qr-ring-color': theme.ringColor,
  } as CSSProperties;

  const handleKakaoShare = useCallback(async () => {
    const result = await shareSessionResultPage({
      title: '나의 스트레스 완화 세션',
      description: '사운드필 키오스크에서 측정한 스트레스 세션 결과입니다.',
      url: shareUrl,
      imageUrl: SESSION_RESULT_OG_IMAGE,
    });
    const message = sessionShareFeedbackMessage(result);
    setShareFeedback(message);
    if (message) {
      window.setTimeout(() => setShareFeedback(null), 4000);
    }
  }, [shareUrl]);

  useEffect(() => {
    if (!autoKakaoShare || autoShareTriggered.current) return;
    autoShareTriggered.current = true;
    void handleKakaoShare();
  }, [autoKakaoShare, handleKakaoShare]);

  return (
    <div className="qr-session-result" style={themeStyle}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={shareDescription} />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SESSION_RESULT_SITE_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:image" content={SESSION_RESULT_OG_IMAGE} />
        <meta property="og:image:secure_url" content={SESSION_RESULT_OG_IMAGE} />
        <meta property="og:image:width" content={String(SESSION_RESULT_OG_IMAGE_WIDTH)} />
        <meta property="og:image:height" content={String(SESSION_RESULT_OG_IMAGE_HEIGHT)} />
        <meta property="og:image:alt" content="사운드필 스트레스 완화 세션 결과" />
        <meta property="og:locale" content="ko_KR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={shareDescription} />
        <meta name="twitter:image" content={SESSION_RESULT_OG_IMAGE} />
      </Helmet>

      <div className="qr-session-result__bg" aria-hidden />

      <div className="qr-session-result__inner">
        <div className="qr-session-result__content">
          <div className="qr-session-result__hero-icon">
            <img
              src={sessionCompleteIcon}
              alt=""
              className="qr-session-result__hero-icon-img"
            />
          </div>
          <header className="qr-session-result__header">
            <h1 className="qr-session-result__title">
              나의 스트레스 완화
              <br />
              세션 기록
            </h1>
            {recordDate ? <p className="qr-session-result__date">{recordDate}</p> : null}
          </header>

          <section className="qr-session-result__cards" aria-label="측정 요약">
            <VitalCard
              icon={<StressFaceIcon level={theme.level} size={32} />}
              iconBg={theme.iconBg}
              ringColor={theme.ringColor}
              label="스트레스 지수"
              value={data.stress}
              subLabel={theme.stressSubLabel}
              accent={theme.accent}
            />
            <VitalCard
              icon={<MetricIconHeart size={28} />}
              iconBg="#f3ebe4"
              ringColor="#f3ebe4"
              label="심박수"
              value={
                <>
                  {displayMetric(data.bpm)}
                  <span className="qr-session-result__unit">bpm</span>
                </>
              }
              subLabel={data.note || undefined}
              accent="#006c49"
            />
            <VitalCard
              icon={<MetricIconHrv size={30} />}
              iconBg="#eaf9f7"
              ringColor="#eaf9f7"
              label="HRV"
              value={displayMetric(data.hrv)}
              subLabel="안정됨"
              accent="#006c49"
            />
            <VitalCard
              icon={<MetricIconClock size={28} />}
              iconBg="#e6edf8"
              ringColor="#e6edf8"
              label="세션 시간"
              value={
                <>
                  {displayMetric(data.min)}
                  <span className="qr-session-result__unit">min</span>
                </>
              }
              accent={theme.accent}
            />
          </section>

          <button
            type="button"
            className="qr-session-result__kakao"
            onClick={() => {
              void handleKakaoShare();
            }}
          >
            <KakaoIcon />
            <span>카카오톡 공유</span>
          </button>
          {shareFeedback ? (
            <p className="qr-session-result__share-feedback" role="status">
              {shareFeedback}
            </p>
          ) : null}

          <div className="qr-session-result__cta-copy">
            <p className="qr-session-result__cta-lead">스트레스 세션, 나도 해보고 싶다면?</p>
            <p className="qr-session-result__cta-title">사운드필에서 계속하세요</p>
          </div>

          <button
            type="button"
            className="qr-session-result__open-btn"
            onClick={() => openSoundPillApp()}
          >
            사운드필 열기
          </button>

          <p className="qr-session-result__promo">
            의학적 근거를 바탕으로
            <br />
            3년 동안 연구한
            <br />
            사운드필을 경험하세요
          </p>

          <footer className="qr-session-result__footer">
            본 리포트는 SoundPill 키오스크 세션 측정 결과이며, 개인 참고용입니다.
          </footer>
        </div>
      </div>
    </div>
  );
}

/** QR 스캔용 공개 세션 결과 — 로그인 불필요 */
export default function SessionResultPage() {
  const [searchParams] = useSearchParams();
  const data = parseSessionResultQuery(searchParams);
  const autoKakaoShare = isKakaoShareEnabled(searchParams);

  if (!data) {
    return (
      <div className="qr-session-result qr-session-result--empty">
        <Helmet>
          <title>세션 결과 | SoundPill</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1>세션 결과를 찾을 수 없어요</h1>
        <p>QR 코드로 다시 스캔하거나, 키오스크에서 세션을 완료한 뒤 공유해 주세요.</p>
        <Link to="/app/home">사운드필 홈으로</Link>
      </div>
    );
  }

  return <SessionResultContent data={data} autoKakaoShare={autoKakaoShare} />;
}
